import i18n from 'i18next'
import type { BackendModule, ReadCallback, ResourceKey } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

/**
 * 언어 리소스 지연 로딩(코드 스플리팅).
 *
 * 이전에는 ko.json(322KB) + en.json(288KB) 을 정적 import 해서 **610KB 가 모든 방문자의
 * 엔트리 청크**에 들어갔다. 공개 SEO 페이지(블로그·사고데이터센터)만 보러 온 방문자도
 * 두 언어 전체를 받았다.
 *
 * 이제 dynamic import() 로 언어별 청크를 분리한다.
 *   - ko 방문자(대다수): ko 청크만 (en 288KB 미전송)
 *   - en 방문자: en + fallback(ko) — fallbackLng 가 ko 라 i18next 가 둘 다 로드
 *
 * FOUC 방지: main.tsx 가 `i18nReady` 를 기다린 뒤에야 React 를 렌더한다.
 * 따라서 "문구가 빈 채로/키가 그대로 보이는" 깜빡임 구간이 존재하지 않는다.
 * (prerender/puppeteer 도 동일 — 렌더 자체가 번역 로드 후 시작되므로 SEO 안전)
 */
const loaders: Record<string, () => Promise<{ default: ResourceKey }>> = {
  ko: () => import('../locales/ko.json') as unknown as Promise<{ default: ResourceKey }>,
  en: () => import('../locales/en.json') as unknown as Promise<{ default: ResourceKey }>,
}

const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => { /* 백엔드 옵션 없음 */ },
  read(language: string, _namespace: string, callback: ReadCallback) {
    const load = loaders[language]
    // 지원하지 않는 코드(예: 'en-US' 원본)로 호출돼도 빈 번들로 안전하게 넘어간다.
    if (!load) {
      callback(null, {})
      return
    }
    load()
      .then((mod) => callback(null, mod.default))
      .catch((err: unknown) => callback(err instanceof Error ? err : new Error(String(err)), false))
  },
}

// 브라우저 언어 자동 감지, 한국어 기본
export const i18nReady = i18n
  .use(lazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'ko',
    supportedLngs: ['ko', 'en'],
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    react: {
      // 렌더 전에 i18nReady 를 기다리므로 Suspense 로 던질 일이 없다.
      // 런타임 언어 전환(EN↔KO 토글, ForceLocale)은 새 언어 로드가 끝난 뒤에야
      // languageChanged 가 발화 → 그 전까지 **이전 언어 문구가 그대로** 보인다(빈 화면 없음).
      useSuspense: false,
    },
  })

export default i18n
